import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { JOB_STATUS_META, APPOINTMENT_TYPE_LABELS, fmtTime } from "./officeMeta";

const VIEW_MODES = ["day", "week", "month"];

const rangeFor = (mode, anchor) => {
  if (mode === "day") return { start: anchor.startOf("day"), end: anchor.endOf("day") };
  if (mode === "week") return { start: anchor.startOf("week"), end: anchor.endOf("week") };
  return { start: anchor.startOf("month").startOf("week"), end: anchor.endOf("month").endOf("week") };
};

const DayAgenda = ({ day, jobs, timeOff, onDayClick, compact }) => {
  const dayJobs = jobs.filter((j) => dayjs(j.scheduled_start).isSame(day, "day"));
  const dayOff = timeOff.filter((t) => dayjs(t.start_at).isSame(day, "day"));
  return (
    <div
      className={`rounded-xl border border-slate-200 p-2 dark:border-white/10 ${compact ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5" : ""}`}
      onClick={compact ? () => onDayClick(day) : undefined}
      data-testid={`office-calendar-day-${day.format("YYYY-MM-DD")}`}
    >
      <p className={`text-xs font-bold ${compact ? "text-slate-500 dark:text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>{day.format(compact ? "D" : "ddd, MMM D")}</p>
      {compact ? (
        (dayJobs.length + dayOff.length) > 0 && (
          <p className="mt-1 text-[11px] font-bold text-[#0052FF]">{dayJobs.length + dayOff.length} item{dayJobs.length + dayOff.length > 1 ? "s" : ""}</p>
        )
      ) : (
        <div className="mt-2 space-y-1.5">
          {dayJobs.length === 0 && dayOff.length === 0 && <p className="text-xs text-slate-400">Nothing scheduled.</p>}
          {dayJobs.map((j) => (
            <div key={j.job_id} className="rounded-lg border border-slate-200 p-2 dark:border-white/10" data-testid={`office-calendar-job-${j.job_id}`}>
              <p className="text-xs font-bold text-slate-900 dark:text-white">{fmtTime(j.scheduled_start)} — {j.title}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">{j.team_name} · {APPOINTMENT_TYPE_LABELS[j.appointment_type]}</p>
              <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${JOB_STATUS_META[j.status].cls}`}>{JOB_STATUS_META[j.status].label}</span>
            </div>
          ))}
          {dayOff.map((t) => (
            <div key={t.time_off_id} className="rounded-lg border border-dashed border-slate-300 p-2 dark:border-white/15" data-testid={`office-calendar-timeoff-${t.time_off_id}`}>
              <p className="text-xs font-bold text-slate-600 dark:text-slate-300">{fmtTime(t.start_at)} — Time Off</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">{t.scope}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OfficeCalendarTab = ({ meta, API, deepLinkScope, personMode }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(dayjs());
  const [scopeKind, setScopeKind] = useState(() => {
    if (personMode) return "individual";
    if (deepLinkScope?.scopeKind && ["organization", "team", "individual"].includes(deepLinkScope.scopeKind)) return deepLinkScope.scopeKind;
    return meta.scope.level === "org" ? "organization" : meta.scope.level === "team" ? "team" : "individual";
  });
  const [teamId, setTeamId] = useState(() => deepLinkScope?.teamId || meta.teams?.[0]?.team_id || "");
  const [targetUserId, setTargetUserId] = useState(() => personMode?.userId || deepLinkScope?.userId || (meta.scope?.level === "self" ? user?.user_id : meta.users?.[0]?.user_id) || "");
  const [status, setStatus] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const range = useMemo(() => rangeFor(mode, anchor), [mode, anchor]);

  const load = useCallback(() => {
    const params = {
      start: range.start.format("YYYY-MM-DDTHH:mm"),
      end: range.end.format("YYYY-MM-DDTHH:mm"),
    };
    if (status) params.status = status;
    if (appointmentType) params.appointment_type = appointmentType;
    if (personMode) {
      axios.get(`${API}/org/people/${personMode.userId}/calendar`, { params, withCredentials: true })
        .then(({ data: resp }) => setData(resp))
        .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
      return;
    }
    params.scope_kind = scopeKind;
    if (scopeKind === "team" && teamId) params.team_id = teamId;
    if (scopeKind === "individual" && targetUserId) params.target_user_id = targetUserId;
    axios.get(`${API}/org/office/calendar`, { params, withCredentials: true })
      .then(({ data: resp }) => setData(resp))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, personMode, scopeKind, teamId, targetUserId, status, appointmentType, range]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const out = [];
    let cur = range.start.startOf("day");
    const end = range.end.startOf("day");
    while (cur.isBefore(end) || cur.isSame(end, "day")) { out.push(cur); cur = cur.add(1, "day"); }
    return out;
  }, [range]);

  const scopeOptions = [
    ...(meta.scope?.level === "org" ? [{ v: "organization", l: "Organization" }] : []),
    ...(meta.scope?.level !== "self" ? [{ v: "team", l: "Team" }] : []),
    { v: "individual", l: "Individual" },
  ];

  return (
    <Panel title="Calendar" subtitle="Every scheduled Job and Time Off block, filtered to what you have access to." testId="office-calendar-tab">
      {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-calendar-error">{error}</p>}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setAnchor((a) => a.subtract(1, mode))} data-testid="office-calendar-prev-btn" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:hover:bg-white/10"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => setAnchor(dayjs())} data-testid="office-calendar-today-btn" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">Today</button>
          <button type="button" onClick={() => setAnchor((a) => a.add(1, mode))} data-testid="office-calendar-next-btn" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:hover:bg-white/10"><ChevronRight size={16} /></button>
          <span className="ml-2 text-sm font-bold text-slate-800 dark:text-slate-100" data-testid="office-calendar-range-label">
            {mode === "month" ? anchor.format("MMMM YYYY") : `${range.start.format("MMM D")} – ${range.end.format("MMM D, YYYY")}`}
          </span>
        </div>
        <div className="flex gap-1" data-testid="office-calendar-view-mode">
          {VIEW_MODES.map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`office-calendar-mode-${m}`}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold capitalize ${mode === m ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {!personMode && (
        <div className="mb-4 flex flex-wrap gap-2">
          <select value={scopeKind} onChange={(e) => setScopeKind(e.target.value)} data-testid="office-calendar-scope-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            {scopeOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          {scopeKind === "team" && (
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="office-calendar-team-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
              {meta.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
            </select>
          )}
          {scopeKind === "individual" && meta.scope.level !== "self" && (
            <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} data-testid="office-calendar-user-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
              {meta.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          )}
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="office-calendar-status-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {meta.job_statuses.map((s) => <option key={s} value={s}>{JOB_STATUS_META[s].label}</option>)}
          </select>
          <select value={appointmentType} onChange={(e) => setAppointmentType(e.target.value)} data-testid="office-calendar-type-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Types</option>
            {meta.appointment_types.map((t) => <option key={t} value={t}>{APPOINTMENT_TYPE_LABELS[t]}</option>)}
          </select>
        </div>
      )}
      {personMode && (
        <div className="mb-4 flex flex-wrap gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="people-calendar-status-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {meta.job_statuses.map((s) => <option key={s} value={s}>{JOB_STATUS_META[s].label}</option>)}
          </select>
          <select value={appointmentType} onChange={(e) => setAppointmentType(e.target.value)} data-testid="people-calendar-type-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Types</option>
            {meta.appointment_types.map((t) => <option key={t} value={t}>{APPOINTMENT_TYPE_LABELS[t]}</option>)}
          </select>
        </div>
      )}

      {!data ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : data.jobs.length === 0 && data.time_off.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Nothing scheduled." message="Jobs and Time Off will appear here once scheduled." testId={personMode ? "people-calendar-empty" : "office-calendar-empty"} />
      ) : mode === "month" ? (
        <div className="grid grid-cols-7 gap-1.5" data-testid={personMode ? "people-calendar-month-grid" : "office-calendar-month-grid"}>
          {days.map((d) => (
            <DayAgenda key={d.format("YYYY-MM-DD")} day={d} jobs={data.jobs} timeOff={data.time_off} compact onDayClick={(day) => { setAnchor(day); setMode("day"); }} />
          ))}
        </div>
      ) : mode === "week" ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-7" data-testid={personMode ? "people-calendar-week-grid" : "office-calendar-week-grid"}>
          {days.map((d) => <DayAgenda key={d.format("YYYY-MM-DD")} day={d} jobs={data.jobs} timeOff={data.time_off} />)}
        </div>
      ) : (
        <div data-testid={personMode ? "people-calendar-day-view" : "office-calendar-day-view"}>
          <DayAgenda day={anchor} jobs={data.jobs} timeOff={data.time_off} />
        </div>
      )}
    </Panel>
  );
};
